import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { delay, filter, tap } from 'rxjs/operators';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import { loadPropertyUsers } from '@main-application/administration/actions/administration.actions';
import {
  selectPropertyUsers,
  selectPropertyUsersLoading,
} from '@main-application/administration/selectors/administration.selectors';
import { TimezoneService } from '@main-application/management/components/system-configuration/components/date-time-configuration/timezone.service';
import {
  addNewTurnoverTicket,
  clearAddedTicketAttachment,
  clearTicketPreview,
  deleteFileFromRepository,
  deleteTurnoverTicketAttachment,
  getTicket,
  getTicketAttachments,
  setTicketAttachment,
  updateTicketPreview,
} from '@main-application/turnovers/actions/turnovers.actions';
import { TurnoverForm } from '@main-application/turnovers/config/enums/turnover-form';
import {
  selectAddedTicketAttachment,
  selectNewTurnoverTicket,
  selectNewTurnoverTicketLoading,
  selectTicket,
  selectTicketAttachments,
  selectTicketUpdateInProgress,
  selectTicketUpdateInProgressStatus,
} from '@main-application/turnovers/selectors/turnovers.selectors';
import { deleteFile } from '@shared/actions/file-upload.actions';
import { TicketAttachmentType } from '@shared/enums/attachment-type';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { PriorityType } from '@shared/enums/priority-type';
import { TicketStatusType } from '@shared/enums/ticket-status-type';
import { TicketType } from '@shared/enums/ticket-type';
import { UpdateStatus } from '@shared/enums/update-status';
import { checkPermissionLevel } from '@shared/functions/check-permission-level.function';
import { getDateObjectFunction } from '@shared/functions/get-date-object.function';
import { getAttachmentToEntityParams } from '@shared/functions/ticket-attachment.utils';
import { AttachmentItem } from '@shared/interfaces/attachment-item';
import { RestGenericTypedAttachment } from '@shared/interfaces/attachment.interface';
import { ListUpdateAction } from '@shared/interfaces/list-update-action.interface';
import { TicketPreviewForm } from '@shared/interfaces/turnover-form.interface';
import { RestTicketModel, RestTypedTicketAttachment } from '@shared/interfaces/turnover.interface';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { TurnoverAttachmentPipe } from '@shared/pipes/turnover-attachment.pipe';
import { selectDeleteStatus } from '@shared/selectors/file-upload.selectors';
import { selectUserData } from '@shared/selectors/user.selectors';

@UntilDestroy()
@Component({
  selector: 'app-ticket-preview-modal',
  templateUrl: './ticket-preview-modal.component.html',
  styleUrls: ['./ticket-preview-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TicketPreviewModalComponent extends ComponentAbstract implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  updateInProgress$: Observable<boolean>;
  addNewInProgress$: Observable<boolean>;
  propertyUserListLoading$: Observable<boolean>;
  users: RestUserModel[] = [];
  attachmentItems: RestGenericTypedAttachment[] = [];
  cleanInputAttachment: EventEmitter<void> = new EventEmitter<void>();
  ticket: RestTicketModel = null;
  newAttachmentItem: AttachmentItem = null;
  isTicketDone = false;
  formSubmitted = false;

  @ViewChild('attachments', { read: ElementRef, static: false }) attachments: ElementRef<HTMLDivElement>;

  constructor(
    protected cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<TicketPreviewModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ListUpdateAction,
    private formBuilder: UntypedFormBuilder,
    private store: Store<{}>,
    private turnoverAttachmentPipe: TurnoverAttachmentPipe,
    private timezoneService: TimezoneService
  ) {
    super(cdr);
  }

  get name(): UntypedFormControl {
    return this.form?.get(TurnoverForm.NAME) as UntypedFormControl;
  }

  get detailedInformation(): UntypedFormControl {
    return this.form.get(TurnoverForm.DETAILED_INFORMATION) as UntypedFormControl;
  }

  get assigneeId(): UntypedFormControl {
    return this.form.get(TurnoverForm.ASSIGNEE_ID) as UntypedFormControl;
  }

  get end(): UntypedFormControl {
    return this.form?.get(TurnoverForm.END) as UntypedFormControl;
  }

  get ticketPriorityType(): UntypedFormControl {
    return this.form.get(TurnoverForm.TICKET_PRIORITY_TYPE) as UntypedFormControl;
  }

  get ticketStatusType(): UntypedFormControl {
    return this.form.get(TurnoverForm.TICKET_STATUS_TYPE) as UntypedFormControl;
  }

  get ableToResolve(): UntypedFormControl {
    return this.form?.get(TurnoverForm.ABLE_TO_RESOLVE) as UntypedFormControl;
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearTicketPreview());
  }

  ngOnInit(): void {
    if (this.data?.isNew) {
      this.initForm();
    } else {
      this.store.dispatch(getTicket({ ticketId: this.data?.ticketId }));
    }
    this.updateInProgress$ = this.store.select(selectTicketUpdateInProgress);
    this.addNewInProgress$ = this.store.select(selectNewTurnoverTicketLoading);
    this.store.dispatch(loadPropertyUsers({ propertyId: this.data?.propertyId }));
    this.propertyUserListLoading$ = this.store.select(selectPropertyUsersLoading);

    this.store
      .select(selectPropertyUsers)
      .pipe(
        untilDestroyed(this),
        filter(users => !!users),
        tap(users => {
          this.users = users;
        })
      )
      .subscribe();

    combineLatest([this.store.select(selectUserData), this.store.select(selectTicket)])
      .pipe(
        untilDestroyed(this),
        filter(([userData, ticket]) => !!userData && !!ticket),
        tap(([userData, ticket]) => {
          this.ticket = ticket;
          this.userData = userData;
          this.attachmentItems = this.turnoverAttachmentPipe.transform(
            this.ticket?.allAttachments,
            TicketAttachmentType.Attachment
          );
          this.initForm();
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.store
      .select(selectAddedTicketAttachment)
      .pipe(
        untilDestroyed(this),
        filter((addedTicketAttachment: RestTypedTicketAttachment) => !!addedTicketAttachment),
        tap((addedTicketAttachment: RestTypedTicketAttachment) => {
          this.cleanInputAttachment.emit();
          this.store.dispatch(clearAddedTicketAttachment());
          if (this.ticket?.id) {
            this.store.dispatch(getTicketAttachments({ ticketId: this.ticket.id }));
          }
        })
      )
      .subscribe();

    this.store
      .select(selectTicketAttachments)
      .pipe(
        untilDestroyed(this),
        filter(ticketAttachments => !!ticketAttachments),
        tap((ticketAttachments: RestTypedTicketAttachment[]) => {
          this.attachmentItems = this.turnoverAttachmentPipe.transform(
            ticketAttachments.map<RestGenericTypedAttachment>(item => {
              return {
                ...item,
                targetAttachmentType: item.ticketAttachmentType,
              };
            }),
            TicketAttachmentType.Attachment
          );
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.store
      .select(selectTicketUpdateInProgressStatus)
      .pipe(
        untilDestroyed(this),
        filter(status => status === UpdateStatus.UPDATE),
        delay(300),
        tap(() => {
          this.dialogRef.close();
        })
      )
      .subscribe();

    this.store
      .select(selectNewTurnoverTicket)
      .pipe(
        untilDestroyed(this),
        filter((newTurnoverTicket: RestTicketModel) => !!newTurnoverTicket),
        tap((newTurnoverTicket: RestTicketModel) => {
          if (this.newAttachmentItem) {
            this.store.dispatch(
              setTicketAttachment({
                ticketType: TicketType.Ticket,
                attachmentToEntity: getAttachmentToEntityParams(this.newAttachmentItem, newTurnoverTicket.id),
              })
            );
            this.newAttachmentItem = null;
            this.store.dispatch(clearAddedTicketAttachment());
          }
          this.dialogRef.close();
        })
      )
      .subscribe();

    this.store
      .select(selectDeleteStatus)
      .pipe(
        untilDestroyed(this),
        filter((status: UpdateStatus) => status === UpdateStatus.DELETED),
        tap(() => {
          this.clearNewTicketAttachment();
        })
      )
      .subscribe();
  }

  deleteTicketAttachment(attachment: RestGenericTypedAttachment) {
    const ticketType = TicketType.Ticket;
    this.store.dispatch(deleteTurnoverTicketAttachment({ ticketType, attachment, ticketId: this.ticket.id }));
  }

  deleteNewTicketAttachment() {
    if (this.newAttachmentItem?.upload) {
      this.store.dispatch(deleteFile({ fileId: this.newAttachmentItem?.upload?.id }));
    } else {
      this.clearNewTicketAttachment();
    }
  }

  addTicketAttachment(attachmentItem: AttachmentItem) {
    if (this.data?.isNew) {
      if (attachmentItem?.upload?.id !== this.newAttachmentItem?.upload?.id && this.newAttachmentItem?.upload?.id) {
        this.store.dispatch(deleteFileFromRepository({ fileId: this.newAttachmentItem?.upload?.id }));
      }
      this.newAttachmentItem = attachmentItem;
      if (this.attachments) {
        setTimeout(() => {
          this.attachments.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
      }
      this.cdr.detectChanges();
    } else {
      this.store.dispatch(
        setTicketAttachment({
          ticketType: TicketType.Ticket,
          attachmentToEntity: getAttachmentToEntityParams(attachmentItem, this.ticket.id),
        })
      );
    }
  }

  cancel() {
    if (this.newAttachmentItem?.upload) {
      this.store.dispatch(deleteFile({ fileId: this.newAttachmentItem?.upload?.id }));
    }
    this.dialogRef.close();
  }

  save() {
    this.formSubmitted = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }

    const formValue: TicketPreviewForm = this.form.value;
    const turnoverTicket: RestTicketModel = {
      ...this.ticket,
      id: formValue[TurnoverForm.ID],
      endDate: formValue[TurnoverForm.END],
      name: formValue[TurnoverForm.NAME],
      detailedInformation: formValue[TurnoverForm.DETAILED_INFORMATION],
      assigneeId: formValue[TurnoverForm.ASSIGNEE_ID],
      ticketPriorityType: formValue[TurnoverForm.TICKET_PRIORITY_TYPE],
      turnoverId: this.data.isNew ? this.data.turnoverId : this.ticket.turnoverId,
    };

    if (this.data.isNew) {
      this.store.dispatch(addNewTurnoverTicket({ turnoverTicket }));
    } else {
      this.store.dispatch(updateTicketPreview({ turnoverTicket }));
    }
  }

  markAsDone() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }

    const formValue: TicketPreviewForm = this.form.value;
    const turnoverTicket: RestTicketModel = {
      ...this.ticket,
      detailedInformation: formValue[TurnoverForm.DETAILED_INFORMATION],
      assigneeId: formValue[TurnoverForm.ASSIGNEE_ID],
      ticketPriorityType: formValue[TurnoverForm.TICKET_PRIORITY_TYPE],
      dateCompleted: this.timezoneService.getCurrentDate(),
      ticketStatusType: TicketStatusType.Done,
    };

    this.store.dispatch(updateTicketPreview({ turnoverTicket }));
  }

  private initForm() {
    this.form = this.formBuilder.group({
      [TurnoverForm.ID]: [this.ticket?.id || 0],
      [TurnoverForm.NAME]: [this.ticket?.name || null, [Validators.required, Validators.maxLength(50)]],
      [TurnoverForm.DETAILED_INFORMATION]: [
        this.ticket?.detailedInformation || null,
        [Validators.required, Validators.maxLength(1000)],
      ],
      [TurnoverForm.ASSIGNEE_ID]: [this.ticket?.assigneeId || this.data.stepAssigneeId, [Validators.required]],
      [TurnoverForm.END]: [getDateObjectFunction(this.ticket?.endDate || new Date()), [Validators.required]],
      [TurnoverForm.TICKET_PRIORITY_TYPE]: [
        this.ticket?.ticketPriorityType || PriorityType.Medium,
        [Validators.required],
      ],
      [TurnoverForm.TICKET_STATUS_TYPE]: [this.ticket?.ticketStatusType || TicketStatusType.NotDone], // todo - add [Validators.required]
      [TurnoverForm.ABLE_TO_RESOLVE]: [
        this.ticket?.createdBy?.id === this.userData?.id ||
          checkPermissionLevel(this.userData?.permissionLevel || [], [
            PermissionLevelType.Tickets_EditAll,
            PermissionLevelType.PropertyManager_ActiveTurnovers_Edit,
          ]),
      ],
    });
    this.isTicketDone = this.ticketStatusType.value === TicketStatusType.Done;
  }

  private clearNewTicketAttachment() {
    this.cleanInputAttachment.emit();
    this.newAttachmentItem = null;
    this.cdr.detectChanges();
  }
}
